// Microsoft sign-in for the Hub, with MSAL and the Hub canvas app registration.
//
// Staff are signed in automatically, once per tab: opening the Hub goes
// straight to Microsoft, which signs a work computer in with its Windows
// sign-in, and anywhere else asks as usual. After Sign out, or if that one
// attempt fails, the page offers a button instead.
//
// Staff sign in once. The same account is used silently to get an access token
// for the Hub API, and again when Para Agent asks to sign in, so the agent
// never shows a second prompt. Tokens are kept in session storage, which is
// cleared when the tab closes. Roles read here only change what the page
// shows; the API checks them again on every request.

import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from '@azure/msal-browser';

// Silent renewal runs in a hidden frame that comes back to this page, which
// only hands Microsoft's response to the Hub (see redirect-bridge.ts).
const SILENT_REDIRECT_PATH = '/redirect.html';

// Silent failures that mean "sign in again", not a fault: MSAL asking for
// interaction, and the hidden frame timing out, coming back empty, or being
// blocked (MSAL 5.23.0 error codes).
const SILENT_FRAME_FAILURES: readonly string[] = [
  BrowserAuthErrorCodes.timedOut,
  BrowserAuthErrorCodes.redirectBridgeEmptyResponse,
  BrowserAuthErrorCodes.blockIframeReload,
];

export function needsInteraction(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) return true;
  return err instanceof BrowserAuthError && SILENT_FRAME_FAILURES.includes(err.errorCode);
}

// The page cannot get a token without the person signing in again, and this
// tab has already used its one automatic redirect. The page offers a button.
export class SignInNeededError extends Error {
  constructor(message = 'Your sign-in needs refreshing.') {
    super(message);
    this.name = 'SignInNeededError';
  }
}

// One automatic redirect to Microsoft per tab, so a renewal that keeps failing
// cannot loop. The flag clears when a token arrives.
export interface FlagStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const REDIRECTED_KEY = 'hub.signin.autoRedirected';

// Storage that cannot be read counts as "already redirected", so it never loops.
export function mayAutoRedirect(store: FlagStore | undefined): boolean {
  try {
    return store !== undefined && store.getItem(REDIRECTED_KEY) !== '1';
  } catch {
    return false;
  }
}

export function markAutoRedirect(store: FlagStore | undefined): void {
  try {
    store?.setItem(REDIRECTED_KEY, '1');
  } catch {
    // Unwritable storage: mayAutoRedirect already treats it as redirected.
  }
}

export function clearAutoRedirect(store: FlagStore | undefined): void {
  try {
    store?.removeItem(REDIRECTED_KEY);
  } catch {
    // Nothing to clear.
  }
}

// The person signed out in this tab, so the Hub must not sign them straight
// back in. A completed sign-in clears it.
const SIGNED_OUT_KEY = 'hub.signin.signedOut';

export function markSignedOut(store: FlagStore | undefined): void {
  try {
    store?.setItem(SIGNED_OUT_KEY, '1');
  } catch {
    // Unwritable storage: mayAutoSignIn already treats it as "do not".
  }
}

export function clearSignedOut(store: FlagStore | undefined): void {
  try {
    store?.removeItem(SIGNED_OUT_KEY);
  } catch {
    // Nothing to clear.
  }
}

// An automatic redirect is allowed only when this tab has not used its one
// and the person has not signed out here. Unreadable storage means no.
export function mayAutoSignIn(store: FlagStore | undefined): boolean {
  if (!mayAutoRedirect(store)) return false;
  try {
    return store?.getItem(SIGNED_OUT_KEY) !== '1';
  } catch {
    return false;
  }
}

export type AutoSignInDecision = 'go' | 'skip';

// Never inside a frame: the Hub only signs in automatically as the top page.
export function autoSignInDecision(store: FlagStore | undefined, framed: boolean): AutoSignInDecision {
  return !framed && mayAutoSignIn(store) ? 'go' : 'skip';
}

function tabStore(): FlagStore | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function isFramed(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

// What boot should do for a visitor with no account, before it shows anything.
export function autoSignInDecisionForThisTab(): AutoSignInDecision {
  return autoSignInDecision(tabStore(), isFramed());
}

export interface HubConfig {
  tenantId: string;
  clientId: string;
  apiScope: string;
}

export function readConfig(env: Record<string, string | undefined>): HubConfig | null {
  const tenantId = env.VITE_TENANT_ID?.trim();
  const clientId = env.VITE_HUB_CLIENT_ID?.trim();
  const apiScope = env.VITE_HUB_API_SCOPE?.trim();
  if (!tenantId || !clientId || !apiScope) return null;
  return { tenantId, clientId, apiScope };
}

let app: PublicClientApplication | undefined;
let config: HubConfig | undefined;

export async function startSignIn(cfg: HubConfig): Promise<AccountInfo | null> {
  config = cfg;
  const client = new PublicClientApplication({
    auth: {
      clientId: cfg.clientId,
      authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
      redirectUri: window.location.origin + '/',
    },
    cache: { cacheLocation: 'sessionStorage' },
  });
  await client.initialize();
  app = client;
  const result = await client.handleRedirectPromise();
  // A completed sign-in ends "signed out" for this tab.
  if (result?.account) clearSignedOut(tabStore());
  const account = result?.account ?? client.getAllAccounts()[0] ?? null;
  if (account) client.setActiveAccount(account);
  return account;
}

export async function signIn(): Promise<void> {
  if (!app || !config) throw new Error('Sign-in is not ready.');
  await app.loginRedirect({ scopes: [config.apiScope] });
}

// The one automatic sign-in for this tab. A redirect that starts leaves the
// page, so this only returns when it did not: 'skipped' when the tab may not
// sign in automatically, 'failed' when MSAL refused or gave up (Back from
// Microsoft restored from the browser cache, an interaction already in
// progress, a frame, or its 30-second navigation timeout).
export async function autoSignIn(): Promise<'skipped' | 'failed'> {
  const store = tabStore();
  if (autoSignInDecision(store, isFramed()) === 'skip') return 'skipped';
  markAutoRedirect(store);
  try {
    await signIn();
  } catch {
    // Reported to the person as "could not sign you in", with the button.
  }
  return 'failed';
}

export async function signOut(): Promise<void> {
  markSignedOut(tabStore());
  await app?.logoutRedirect();
}

export function currentAccount(): AccountInfo | null {
  return app?.getActiveAccount() ?? null;
}

export function accountRoles(account: AccountInfo | null): string[] {
  const roles = account?.idTokenClaims?.roles;
  return Array.isArray(roles) ? roles.filter((r): r is string => typeof r === 'string') : [];
}

// A token for any scope, without a prompt. Returns null rather than prompting,
// so a caller can fall back instead of interrupting the page.
export async function tokenFor(scope: string): Promise<string | null> {
  const account = currentAccount();
  if (!app || !account) return null;
  try {
    const result = await app.acquireTokenSilent({
      scopes: [scope],
      account,
      redirectUri: window.location.origin + SILENT_REDIRECT_PATH,
    });
    return result.accessToken;
  } catch (err) {
    if (needsInteraction(err)) return null;
    throw err;
  }
}

export async function apiToken(): Promise<string> {
  if (!config) throw new Error('Sign-in is not ready.');
  const store = tabStore();
  const token = await tokenFor(config.apiScope);
  if (token) {
    clearAutoRedirect(store);
    return token;
  }
  // After Sign out, even a stale page reached by Back does not sign back in.
  if (!mayAutoSignIn(store)) throw new SignInNeededError();
  markAutoRedirect(store);
  await signIn();
  throw new SignInNeededError('Signing you in again.');
}
